import osmtogeojson from 'osmtogeojson';
import { osmSearch, osmSearchByBbox } from '../api/osmDataApi';

const filterFeatures = (geoJson, predicate) => {
  if (!geoJson || !Array.isArray(geoJson.features)) {
    return { type: 'FeatureCollection', features: [] };
  }
  return {
    type: 'FeatureCollection',
    features: geoJson.features.filter(
      (f) => f != null && f.geometry != null && f.properties != null && predicate(f),
    ),
  };
};

const hasNoSidewalk = (f) =>
  f.properties.sidewalk === 'no' ||
  f.properties['sidewalk:both'] === 'no' ||
  (f.properties['sidewalk:left'] === 'no' && f.properties['sidewalk:right'] === 'no');

const splitRoads = (geoJson) => ({
  theoretical: filterFeatures(geoJson, (f) => !hasNoSidewalk(f)),
  noSidewalk: filterFeatures(geoJson, hasNoSidewalk),
});

const isLeftDescribed = (f) =>
  f.properties.sidewalk === 'left' ||
  f.properties.sidewalk === 'both' ||
  f.properties['sidewalk:left'] === 'yes';

const isRightDescribed = (f) =>
  f.properties.sidewalk === 'right' ||
  f.properties.sidewalk === 'both' ||
  f.properties['sidewalk:right'] === 'yes';

const splitSidewalks = (geoJson) => ({
  drawn: filterFeatures(geoJson, (f) => f.properties.footway === 'sidewalk'),
  described: filterFeatures(
    geoJson,
    (f) => isLeftDescribed(f) !== isRightDescribed(f),
  ),
  describedBoth: filterFeatures(geoJson, (f) => isLeftDescribed(f) && isRightDescribed(f)),
});

const WALK_HIGHWAYS = ['footway', 'pedestrian', 'living_street', 'path', 'track', 'steps'];

const splitPathsAndCrossings = (geoJson) => ({
  walkPath: filterFeatures(
    geoJson,
    (f) =>
      f.geometry.type === 'LineString' &&
      WALK_HIGHWAYS.includes(f.properties.highway) &&
      f.properties.footway !== 'sidewalk' &&
      f.properties.footway !== 'crossing',
  ),
  walkCyclePath: filterFeatures(
    geoJson,
    (f) =>
      f.geometry.type === 'LineString' &&
      f.properties.highway === 'cycleway' &&
      ['yes', 'designated', 'permissive'].includes(f.properties.foot),
  ),
  crossingPoint: filterFeatures(
    geoJson,
    (f) => f.geometry.type === 'Point' && f.properties.highway === 'crossing',
  ),
  crossingPath: filterFeatures(
    geoJson,
    (f) =>
      f.geometry.type === 'LineString' &&
      f.properties.highway === 'footway' &&
      f.properties.footway === 'crossing',
  ),
});

export const fetchRoadsGeo = async (osmID) => {
  const results = await osmSearch.searchRoads(osmID);
  return splitRoads(osmtogeojson(results));
};

export const fetchSidewalksGeo = async (osmID, startDate) => {
  const results = await osmSearch.searchSidewalks(osmID, startDate);
  return splitSidewalks(osmtogeojson(results));
};

export const fetchPathsAndCrossingsGeo = async (osmID, startDate) => {
  const results = await osmSearch.searchPathsAndCrossings(osmID, startDate);
  return splitPathsAndCrossings(osmtogeojson(results));
};

export const fetchRoadsGeoByBbox = async (bbox) => {
  const results = await osmSearchByBbox.searchRoads(bbox);
  return splitRoads(osmtogeojson(results));
};

export const fetchSidewalksGeoByBbox = async (bbox, startDate) => {
  const results = await osmSearchByBbox.searchSidewalks(bbox, startDate);
  return splitSidewalks(osmtogeojson(results));
};

export const fetchPathsAndCrossingsGeoByBbox = async (bbox, startDate) => {
  const results = await osmSearchByBbox.searchPathsAndCrossings(bbox, startDate);
  return splitPathsAndCrossings(osmtogeojson(results));
};
